import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import useContracts from '../../../../components/layout/ContractSelector/hook/useContracts';
import {
    useLazyGetCitiesQuery,
    useLazyGetEmpresasQuery,
    useGetProvincesQuery,
    useGetCIIUCodesQuery
} from '../../../../services/companyApi';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import dayjs, { Dayjs } from 'dayjs';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { enqueueSnackbar } from 'notistack';
import {
    useCreateVisitaMutation,
    usePatchVisitaMutation,
    useLazyGetVisitasQuery,
    useGetTiposVisitasQuery
} from '../../../../services/visitasApi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLazyGetPresentacionesByEstablecimientoQuery } from '../../../../services/presentacionesApi';
import { useGetOrganismsQuery } from '../../../../services/organismsApi';
import useOrganizationSelector from '../../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { Location } from 'react-router-dom';
import { calcularFechaLimite } from '../../../../utils/fechaLimiteVisita';
import { fetchFeriadosSet } from '../../../../services/feriadosApi';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from '../../consts/visitTypeDisplayNames';
import { esVisitaAbierta } from '../../consts/visitasStatus';
import {
    PrecargaSeguimiento,
    resolverPrecargaSeguimiento,
    tieneSeguimientoVerosimilitudActivo,
    TIPO_VISITA_SEGUIMIENTO_VEROSIMILITUD_ID
} from '../utils/seguimientoVerosimilitud';

// Estados en los que una visita se considera "concluida": si un establecimiento tiene una
// visita de un tipo en alguno de estos estados, se permite volver a seleccionar ese mismo
// tipo de visita. Si está en cualquier otro estado (activa/en curso), se bloquea la selección.

interface FormData {
    observaciones: string;
    nroContrato: { id: number; nombre: string; value: number } | null;
    idOrganismo: { id: number; nombre: string; value: number } | null;
    provincia: { id: number; nombre: string; value: number } | null;
    idProvincia: { id: number; nombre: string; value: number } | null;
    ciudad: { id: number; nombre: string; value: number } | null;
    codigoPostal: { id: number; nombre: string; value: number } | null;
    idTipoVisita: Array<{ id: number; nombre: string; value: number }>;
    idPreventor: { id: number; nombre: string; value: number } | null;
    idProveedor: number | null;
    fechaLimite: Dayjs | null;
    idEstablecimientos: number[];
    distanciaMayor100km: boolean | null;
}

interface FiltersEstablecimientos {
    codigoSRT: string;
    nombre: string;
    calle: string;
    idOrganizacion: { id: number; nombre: string; value: number } | null;
    idCiiu: { id: number; codigo: string; descripcion: string } | null;
}

const parseApiDateAsCalendarDay = (date?: string | null) =>
    date ? dayjs(date.split('T')[0]) : null;

const formatCalendarDayForApi = (date: Dayjs | null) =>
    date ? `${date.format('YYYY-MM-DD')}T00:00:00.000Z` : '';

const formatDataForSubmission = (data: FormData, linkedSiniestroId?: number | null) => {
    const payload: any = {
        observacionAlta: data.observaciones,
        idsTipoVisita: data.idTipoVisita.map((t) => t.id),
        fechaLimite: formatCalendarDayForApi(data.fechaLimite)
    };

    if (linkedSiniestroId) {
        payload.idSiniestro = linkedSiniestroId;
    }

    if (data.idEstablecimientos.length > 0) {
        payload.idsEstablecimiento = data.idEstablecimientos;
    }

    if (data.idPreventor?.id) {
        payload.idPreventor = data.idPreventor.id;
    }

    if (data.idProveedor) {
        payload.idProveedor = data.idProveedor;
    }

    if (data.distanciaMayor100km !== null && data.distanciaMayor100km !== undefined) {
        payload.distanciaMayor100km = data.distanciaMayor100km;
    }

    return payload;
};

const mapVisitToFormData = (
    visit: any,
    fallbackEstablishmentId: number | null = null
): FormData => {
    const source = visit?.raw || visit || {};
    const establishment = source?.establecimiento || visit?.establecimiento || null;
    const organismCentral = establishment?.organismo?.organismoCentral;
    const organism = establishment?.organismo;
    const province = establishment?.direccion?.localidad?.provincia;
    const locality = establishment?.direccion?.localidad;
    const preventor = source?.preventor || visit?.preventor || null;
    const activities = Array.isArray(source?.actividades) ? source.actividades : [];
    const uniqueTipoVisita = new Map<number, { id: number; nombre: string; value: number }>();

    activities.forEach((actividad: any) => {
        const tipoVisita = actividad?.tipoVisita;
        if (tipoVisita?.id && !uniqueTipoVisita.has(tipoVisita.id)) {
            uniqueTipoVisita.set(tipoVisita.id, {
                id: tipoVisita.id,
                nombre:
                    VISIT_TYPE_DISPLAY_NAME_OVERRIDES[tipoVisita.id] ??
                    (tipoVisita.descripcion?.toUpperCase() || String(tipoVisita.id)),
                value: tipoVisita.id
            });
        }
    });

    if (uniqueTipoVisita.size === 0 && source?.tipoVisita?.id) {
        uniqueTipoVisita.set(source.tipoVisita.id, {
            id: source.tipoVisita.id,
            nombre:
                VISIT_TYPE_DISPLAY_NAME_OVERRIDES[source.tipoVisita.id] ??
                (source.tipoVisita.descripcion?.toUpperCase() || String(source.tipoVisita.id)),
            value: source.tipoVisita.id
        });
    }

    const contractValue =
        organismCentral?.nroContrato ?? organismCentral?.contrato ?? organismCentral?.id ?? null;

    return {
        observaciones: source?.observacionAlta || source?.observacion || '',
        nroContrato: organismCentral
            ? {
                  id: organismCentral.id ?? Number(contractValue) ?? 0,
                  nombre: organismCentral.nombre || String(contractValue ?? ''),
                  value: Number(contractValue ?? organismCentral.id ?? 0)
              }
            : null,
        idOrganismo: organism
            ? {
                  id: organism.id,
                  nombre: organism.nombre,
                  value: organism.id
              }
            : null,
        provincia: province
            ? {
                  id: province.id,
                  nombre: province.nombre,
                  value: province.id
              }
            : null,
        idProvincia: province
            ? {
                  id: province.id,
                  nombre: province.nombre,
                  value: province.id
              }
            : null,
        ciudad: locality
            ? {
                  id: locality.id,
                  nombre: locality.nombre,
                  value: locality.id
              }
            : null,
        codigoPostal: locality?.codigoPostal
            ? {
                  id: locality.codigoPostal,
                  nombre: String(locality.codigoPostal),
                  value: locality.codigoPostal
              }
            : null,
        idTipoVisita: Array.from(uniqueTipoVisita.values()),
        idPreventor: preventor
            ? {
                  id: preventor.idUsuario ?? preventor.id,
                  nombre: `${preventor.nombre || ''} ${preventor.apellido || ''}`.trim(),
                  value: preventor.idUsuario ?? preventor.id
              }
            : null,
        fechaLimite: parseApiDateAsCalendarDay(source?.fechaLimite),
        idProveedor:
            preventor?.proveedor?.id || source?.proveedorId || source?.idProveedor || null,
        idEstablecimientos: establishment?.id
            ? [establishment.id]
            : fallbackEstablishmentId
              ? [fallbackEstablishmentId]
              : [],
        distanciaMayor100km: null
    };
};

function useCreateVisita(
    selectedEstablishmentFromDetail: any = null,
    location?: Location,
    selectedVisitaFromState: any = null,
    isEditMode = false,
    isReadOnlyMode = false
) {
    const linkedSiniestroId = location?.state?.idSiniestro || null;
    const linkedIdTipoSiniestro: number | null = location?.state?.idTipoSiniestro ?? null;
    const linkedFechaAltaRoam: string | null = location?.state?.fechaAltaRoam ?? null;
    const linkedPatologias: string | null = location?.state?.patologiasTrazadoras ?? null;
    const linkedEstablishmentId =
        Number(location?.state?.idEstablecimiento || selectedEstablishmentFromDetail?.id) || null;
    const [step, setStep] = useState(isEditMode || isReadOnlyMode || linkedSiniestroId ? 2 : 1);
    const [fechaLimiteEditadaManualmente, setFechaLimiteEditadaManualmente] = useState(false);
    const [feriados, setFeriados] = useState<Set<string>>(new Set());
    const [feriadosCargados, setFeriadosCargados] = useState(false);
    const dispatch = useAppDispatch();

    const [createVisita, { isLoading }] = useCreateVisitaMutation();
    const [patchVisita, { isLoading: isPatchingVisita }] = usePatchVisitaMutation();
    const { t } = useTranslation('visitas');
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>(() =>
        (isEditMode || isReadOnlyMode) && selectedVisitaFromState
            ? mapVisitToFormData(
                  selectedVisitaFromState,
                  linkedEstablishmentId || selectedEstablishmentFromDetail?.id || null
              )
            : {
                  observaciones: '',
                  nroContrato: null,
                  idOrganismo: null,
                  provincia: null,
                  idProvincia: null,
                  ciudad: null,
                  codigoPostal: null,
                  idTipoVisita: [],
                  idPreventor: null,
                  idProveedor: null,
                  fechaLimite: null,
                  idEstablecimientos: linkedEstablishmentId ? [linkedEstablishmentId] : [],
                  distanciaMayor100km: false
              }
    );
    useEffect(() => {
        if (!(isEditMode || isReadOnlyMode) || !selectedVisitaFromState) return;

        setFormData(
            mapVisitToFormData(
                selectedVisitaFromState,
                linkedEstablishmentId || selectedEstablishmentFromDetail?.id || null
            )
        );
    }, [selectedVisitaFromState?.id]);

    const [getPresentacionesByEstablecimiento] = useLazyGetPresentacionesByEstablecimientoQuery();
    const { data: { data: { data: organismsData } } = { data: { data: [] } } } =
        useGetOrganismsQuery({ skip: 0, take: 1000 });
    const [organisms, setOrganisms] = useState([]);

    const [
        fetchEstablishments,
        {
            data: establishmentsData,
            isFetching: isFetchingEstablishments,
            isLoading: isLoadingEstablishments
        }
    ] = useLazyGetEmpresasQuery();

    const { data: ciiuCodesData, isFetching: isFetchingCiiuCodes } = useGetCIIUCodesQuery(null);
    const ciiuCodesOptions = ciiuCodesData?.data?.codigos || [];

    const { organizations: organizationsOptions } = useOrganizationSelector();

    const cleanEstablecimientosFilters = () => {
        setFiltersEstablecimientos({
            codigoSRT: '',
            nombre: '',
            calle: '',
            idOrganizacion: null,
            idCiiu: null
        });

        setFormData((prev) => ({
            ...prev,
            provincia: null,
            idProvincia: null,
            ciudad: null,
            codigoPostal: null,
            nroContrato: null,
            idOrganismo: null
        }));
    };

    const [filtersEstablecimientos, setFiltersEstablecimientos] = useState<FiltersEstablecimientos>(
        {
            codigoSRT: '',
            nombre: location?.state?.establishmentName || '',
            calle: '',
            idOrganizacion: null,
            idCiiu: null
        }
    );

    const { contracts, isFetching: isFetchingContracts } = useContracts(
        filtersEstablecimientos.idOrganizacion?.id ?? null
    );

    const handleFiltersEstablecimientosChange = (name: string, value: any) => {
        setFiltersEstablecimientos((prev) => ({
            ...prev,
            [name]: value
        }));

        if (name === 'idOrganizacion') {
            setOrganisms(allOrganisms);
            setFormData((prev) => ({
                ...prev,
                nroContrato: null,
                idOrganismo: null
            }));
        }
    };

    const [loadingValidacionRGRL, setLoadingValidacionRGRL] = useState(false);
    const [allOrganisms, setAllOrganisms] = useState([]);

    useEffect(() => {
        setAllOrganisms(organismsData || []);
        setOrganisms(organismsData || []);
    }, [organismsData]);

    const [establecimientosPagination, setEstablecimientosPagination] = useState({
        page: 0,
        rowsPerPage: 500
    });

    const handleChangeEstablecimientosPage = useCallback((newPage: number) => {
        setEstablecimientosPagination((prev) => ({ ...prev, page: newPage }));
    }, []);

    const handleChangeEstablecimientosRowsPerPage = useCallback((newRowsPerPage: number) => {
        setEstablecimientosPagination({ page: 0, rowsPerPage: newRowsPerPage });
    }, []);

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const filtersEstablecimientosKey = JSON.stringify([
        formData.nroContrato?.id,
        formData.idProvincia,
        formData.ciudad?.id,
        formData.ciudad?.nombre,
        formData.idOrganismo?.id,
        filtersEstablecimientos.codigoSRT,
        filtersEstablecimientos.nombre,
        filtersEstablecimientos.calle,
        filtersEstablecimientos.idOrganizacion,
        filtersEstablecimientos.idCiiu
    ]);
    const prevFiltersEstablecimientosKeyRef = useRef(filtersEstablecimientosKey);

    // Cada vez que cambia un filtro se vuelve a la primera página del paginador de
    // establecimientos, en vez de conservar el skip anterior sobre el nuevo universo filtrado.
    useEffect(() => {
        const filtrosCambiaron =
            prevFiltersEstablecimientosKeyRef.current !== filtersEstablecimientosKey;
        prevFiltersEstablecimientosKeyRef.current = filtersEstablecimientosKey;

        if (filtrosCambiaron && establecimientosPagination.page !== 0) {
            setEstablecimientosPagination((prev) => ({ ...prev, page: 0 }));
            return;
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            fetchEstablishments({
                skip: establecimientosPagination.page * establecimientosPagination.rowsPerPage,
                take: establecimientosPagination.rowsPerPage,
                idOrganismoCentral: formData.nroContrato?.id || undefined,
                idProvincia: formData.idProvincia
                    ? [(formData.idProvincia as any)?.id ?? formData.idProvincia]
                    : undefined,
                localidad: formData.ciudad?.nombre,
                idOrganismo: formData.idOrganismo?.id,
                codigoSRT: filtersEstablecimientos.codigoSRT || undefined,
                nombre: filtersEstablecimientos.nombre || undefined,
                calle: filtersEstablecimientos.calle || undefined,
                idOrganizacion: filtersEstablecimientos.idOrganizacion?.id || undefined,
                idCiiu: filtersEstablecimientos.idCiiu?.id || undefined
            });
        }, 400);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
        // filtersEstablecimientosKey serializa todos los campos de filtro leídos arriba: agregarlos
        // individualmente sería redundante y perdería la comparación estable por valor.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        fetchEstablishments,
        filtersEstablecimientosKey,
        establecimientosPagination.page,
        establecimientosPagination.rowsPerPage
    ]);

    const establecimientosTotalCount = establishmentsData?.data?.count ?? 0;

    const establishments = useMemo(
        () =>
            Array.isArray(establishmentsData?.data?.data)
                ? establishmentsData.data?.data.map((est: any) => ({
                      id: est.id,
                      codigoSRT: est.codigoSRT,
                      nombre: est.nombre,
                      contrato: est.organismo?.nombre,
                      ciiuCodigo: est.ciiu?.codigo,
                      ciiuNombre: est.ciiu?.descripcion,
                      estado: est.estado,
                      localidad: est.direccion?.localidad || 'N/A',
                      postalCode: est.direccion?.localidad?.codigoPostal || 'N/A',
                      direccion: {
                          calle: est.direccion?.calle ?? '',
                          altura: est.direccion?.altura ?? '',
                          departamento: est.direccion?.departamento ?? null
                      }
                  }))
                : [],
        [establishmentsData]
    );

    // Como la grilla trae los establecimientos de a una página/filtro, un establecimiento
    // seleccionado puede dejar de estar en `establishments` al cambiar de página o filtro. Este
    // cache acumula el detalle completo ya visto (por id) para poder listar TODOS los
    // seleccionados en el Paso 2 y en el drawer, sin importar en qué página/filtro se hayan
    // tildado (antes solo se guardaban codigoSRT/nombre/contrato, insuficiente para las cards).
    const [establecimientosDetalleCache, setEstablecimientosDetalleCache] = useState<
        Record<number, (typeof establishments)[number]>
    >({});

    useEffect(() => {
        if (establishments.length === 0) return;

        setEstablecimientosDetalleCache((prev) => {
            const next = { ...prev };
            establishments.forEach((est: any) => {
                next[est.id] = est;
            });
            return next;
        });
    }, [establishments]);

    const selectedEstablishmentsDetails = useMemo(
        () =>
            formData.idEstablecimientos
                .map((id) => establecimientosDetalleCache[id])
                .filter((detalle): detalle is (typeof establishments)[number] => Boolean(detalle)),
        [formData.idEstablecimientos, establecimientosDetalleCache]
    );

    const handleSelectEstablishment = useCallback((establishmentId: number) => {
        setFormData((prev) => ({
            ...prev,
            idEstablecimientos: prev.idEstablecimientos.includes(establishmentId)
                ? prev.idEstablecimientos.filter((id) => id !== establishmentId)
                : [...prev.idEstablecimientos, establishmentId]
        }));
    }, []);

    const handleSelectEstablishments = useCallback(
        (establishmentIds: number[], selected: boolean) => {
            setFormData((prev) => {
                const nextSelected = new Set(prev.idEstablecimientos);

                establishmentIds.forEach((id) => {
                    if (selected) {
                        nextSelected.add(id);
                    } else {
                        nextSelected.delete(id);
                    }
                });

                return {
                    ...prev,
                    idEstablecimientos: Array.from(nextSelected)
                };
            });
        },
        []
    );

    const handleContractSelection = (value: any) => {
        const filteredOrganisms = value
            ? allOrganisms.filter((org: any) => org?.idOrganismoCentral === value?.id)
            : allOrganisms;

        setOrganisms(filteredOrganisms);
        setFormData((prev) => ({
            ...prev,
            nroContrato: value?.id ? value : null,
            idOrganismo: null
        }));
    };

    const { data: provincesList } = useGetProvincesQuery(null);
    const provinces =
        provincesList?.data?.provincias?.map((province: { id: number; nombre: string }) => ({
            nombre: province.nombre,
            id: province.id,
            value: province.id
        })) || [];

    const [getCities, { data: citiesList }] = useLazyGetCitiesQuery();
    useEffect(() => {
        if (formData.idProvincia) {
            getCities({ provinceId: (formData.idProvincia as any)?.id ?? formData.idProvincia });
        }
    }, [formData.idProvincia, getCities]);

    const cities = Array.isArray(citiesList?.data?.localidades)
        ? citiesList.data.localidades.map(
              (city: { id: number; nombre: string; codigoPostal: number }) => ({
                  nombre: city.nombre,
                  id: city.id,
                  value: city.id,
                  codigoPostal: city.codigoPostal
              })
          )
        : [];

    useEffect(() => {
        if (!linkedFechaAltaRoam || !linkedSiniestroId) return;
        const año = parseInt(linkedFechaAltaRoam.slice(0, 4), 10);
        let cancelled = false;
        (async () => {
            try {
                const result = await fetchFeriadosSet(año, año + 1);
                if (!cancelled && result instanceof Set) {
                    setFeriados(result);
                    setFeriadosCargados(true);
                }
            } catch (_) {
                if (!cancelled && linkedPatologias?.trim().toLowerCase() === 'muerte') {
                    enqueueSnackbar(
                        'No se pudo cargar el calendario de feriados. Ingresá la fecha de visita manualmente.',
                        { variant: 'warning' }
                    );
                }
            }
        })();
        return () => { cancelled = true; };
    }, [linkedFechaAltaRoam, linkedSiniestroId]);

    useEffect(() => {
        if (!linkedSiniestroId || fechaLimiteEditadaManualmente) return;
        const isMortal = linkedPatologias?.trim().toLowerCase() === 'muerte';
        if (isMortal && !feriadosCargados) return;
        const fechaCalculada = calcularFechaLimite(
            linkedFechaAltaRoam,
            linkedIdTipoSiniestro,
            linkedPatologias,
            formData.distanciaMayor100km,
            feriados
        );
        if (fechaCalculada) {
            setFormData((prev) => ({ ...prev, fechaLimite: fechaCalculada }));
        }
    }, [linkedFechaAltaRoam, linkedIdTipoSiniestro, linkedPatologias, formData.distanciaMayor100km, linkedSiniestroId, fechaLimiteEditadaManualmente, feriados, feriadosCargados]);

    const handleChange = (event: { target: { name: any; value: any } }) => {
        const { name, value } = event.target;

        if (name === 'fechaLimite') {
            setFechaLimiteEditadaManualmente(true);
        }

        if (name === 'distanciaMayor100km') {
            setFechaLimiteEditadaManualmente(false);
        }

        if (name === 'idProvincia') {
            setFormData((prev) => ({
                ...prev,
                idProvincia: value?.id || null,
                ciudad: null,
                codigoPostal: null,
                provincia: value
            }));
        } else if (name === 'ciudad') {
            setFormData((prev) => ({
                ...prev,
                ciudad: value,
                codigoPostal: value
                    ? {
                          id: value.id,
                          nombre: value.codigoPostal.toString(),
                          value: value.codigoPostal
                      }
                    : null
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleClearSelection = () => {
        setFormData((prev) => ({
            ...prev,
            idEstablecimientos: []
        }));
    };

    const idEstablecimientosKey = formData.idEstablecimientos.join(',');

    const [triggerGetVisitasPorEstablecimientos] = useLazyGetVisitasQuery();

    // Tipos de visita de siniestro (ROAM): no se les aplica la validación de "visita del
    // mismo tipo en curso" (SMP-331).
    const { data: tiposVisitasData } = useGetTiposVisitasQuery(null);
    const idsTiposVisitaSiniestro = useMemo(
        () =>
            new Set<number>(
                (tiposVisitasData?.data || [])
                    .filter((tipo: any) => tipo?.requiereSiniestro === true)
                    .map((tipo: any) => tipo.id)
            ),
        [tiposVisitasData]
    );

    // Cache acumulado de visitas por establecimiento: una vez consultado un establecimiento
    // no se vuelve a pedir (p.ej. al des-seleccionar y volver a seleccionar, o al quitar otro
    // establecimiento de la lista), la info ya obtenida se conserva.
    const [visitasPorEstablecimiento, setVisitasPorEstablecimiento] = useState<
        Record<number, any[]>
    >({});
    const idsEstablecimientosConsultadosRef = useRef<Set<number>>(new Set());
    const [isFetchingVisitasEstablecimientos, setIsFetchingVisitasEstablecimientos] =
        useState(false);

    useEffect(() => {
        if (step !== 2 || formData.idEstablecimientos.length === 0) return;

        const idsFaltantes = formData.idEstablecimientos.filter(
            (id) => !idsEstablecimientosConsultadosRef.current.has(id)
        );
        if (idsFaltantes.length === 0) return;

        idsFaltantes.forEach((id) => idsEstablecimientosConsultadosRef.current.add(id));
        setIsFetchingVisitasEstablecimientos(true);

        triggerGetVisitasPorEstablecimientos({
            idsEstablecimientos: idsFaltantes,
            skip: 0,
            take: 999999
        })
            .then((resultado: any) => {
                const nuevoMapa: Record<number, any[]> = {};
                // Los establecimientos sin visitas también quedan marcados (array vacío) para
                // no volver a consultarlos.
                idsFaltantes.forEach((id) => {
                    nuevoMapa[id] = [];
                });

                (resultado?.data?.data?.data || []).forEach((visita: any) => {
                    const idEstablecimiento = visita?.idEstablecimiento;
                    if (!idEstablecimiento) return;

                    if (!nuevoMapa[idEstablecimiento]) {
                        nuevoMapa[idEstablecimiento] = [];
                    }
                    nuevoMapa[idEstablecimiento].push(visita);
                });

                setVisitasPorEstablecimiento((prev) => ({ ...prev, ...nuevoMapa }));
            })
            .finally(() => setIsFetchingVisitasEstablecimientos(false));
    }, [step, idEstablecimientosKey, triggerGetVisitasPorEstablecimientos]);

    // Busca, entre TODOS los establecimientos seleccionados, cuáles tienen una visita del
    // mismo tipo que `idTipoVisita` en un estado NO concluido (una por establecimiento, el
    // primer match). Las visitas "MÚLTIPLE" (con más de una actividad) SÍ se consideran: alcanza
    // con que alguna de sus actividades sea del tipo buscado. Los tipos de siniestro quedan
    // excluidos.
    const findTipoVisitaConflictos = useCallback(
        (idTipoVisita: number) => {
            const conflictos: any[] = [];
            if (idsTiposVisitaSiniestro.has(idTipoVisita)) return conflictos;

            formData.idEstablecimientos.forEach((idEstablecimiento) => {
                const visitasEstablecimiento = visitasPorEstablecimiento[idEstablecimiento] || [];

                const visitaConflicto = visitasEstablecimiento.find((visita: any) => {
                    if (!esVisitaAbierta(visita?.estado)) return false;

                    const actividades = visita?.actividades || [];
                    if (actividades.length === 0) {
                        return visita?.idTipoVisita === idTipoVisita;
                    }

                    return actividades.some(
                        (actividad: any) => actividad?.tipoVisita?.id === idTipoVisita
                    );
                });

                if (visitaConflicto) conflictos.push(visitaConflicto);
            });

            return conflictos;
        },
        [formData.idEstablecimientos, visitasPorEstablecimiento, idsTiposVisitaSiniestro]
    );

    // Ids de establecimientos y de tipos de visita que colisionan con la selección actual:
    // se usan para resaltarlos (chip del tipo, borde de la tarjeta, badge de la visita abierta
    // en conflicto) y para bloquear la generación de la visita hasta que se resuelvan.
    const { establecimientosIncompatibles, tiposVisitaIncompatibles } = useMemo(() => {
        const establecimientos = new Set<number>();
        const tipos = new Set<number>();

        formData.idTipoVisita.forEach((tipo) => {
            const conflictos = findTipoVisitaConflictos(tipo.id);
            if (conflictos.length === 0) return;

            tipos.add(tipo.id);
            conflictos.forEach((visita: any) => {
                if (visita?.idEstablecimiento) establecimientos.add(visita.idEstablecimiento);
            });
        });

        return { establecimientosIncompatibles: establecimientos, tiposVisitaIncompatibles: tipos };
    }, [formData.idTipoVisita, findTipoVisitaConflictos]);

    // Mientras no se hayan consultado las visitas de todos los establecimientos seleccionados
    // no se puede garantizar que `establecimientosIncompatibles` esté completo: bloquea la
    // generación de la visita para evitar el hueco donde el botón queda habilitado sin haber
    // terminado de validar (p.ej. justo al entrar al Paso 2).
    const hayVisitasPendientesDeCargar = formData.idEstablecimientos.some(
        (id) => visitasPorEstablecimiento[id] === undefined
    );

    // Fecha de seguimiento de la verosimilitud de origen: la fecha límite de un Seguimiento
    // Verosimilitud no puede ser anterior (SMP-351).
    const [fechaMinimaSeguimiento, setFechaMinimaSeguimiento] = useState<Dayjs | null>(null);

    // Valida que se pueda dar de alta un Seguimiento Verosimilitud para lo seleccionado en el
    // Paso 1 y devuelve los datos a precargar, o null si se bloquea (ya avisado al usuario).
    const validarSeguimientoVerosimilitud = useCallback(():
        | Extract<PrecargaSeguimiento, { resultado: 'OK' }>
        | null => {
        if (formData.idEstablecimientos.length !== 1) {
            dispatch(
                showAlert({
                    title: t('alerts.seguimientoVerosimilitud.unicoEstablecimiento.title'),
                    message: t('alerts.seguimientoVerosimilitud.unicoEstablecimiento.message'),
                    confirmText: t(
                        'alerts.seguimientoVerosimilitud.unicoEstablecimiento.confirmText'
                    ),
                    icon: 'warning'
                })
            );
            return null;
        }

        const visitas = visitasPorEstablecimiento[formData.idEstablecimientos[0]];
        if (!visitas) {
            enqueueSnackbar(t('alerts.seguimientoVerosimilitud.cargandoVisitas'), {
                variant: 'info'
            });
            return null;
        }

        if (tieneSeguimientoVerosimilitudActivo(visitas)) {
            const nombre =
                visitas.find((visita: any) => visita?.nombreEstablecimiento)
                    ?.nombreEstablecimiento ?? '';
            enqueueSnackbar(t('alerts.seguimientoVerosimilitud.seguimientoActivo', { nombre }), {
                variant: 'error'
            });
            return null;
        }

        const precarga = resolverPrecargaSeguimiento(visitas);

        if (precarga.resultado === 'SIN_VEROSIMILITUD') {
            dispatch(
                showAlert({
                    title: t('alerts.seguimientoVerosimilitud.sinVerosimilitud.title'),
                    message: t('alerts.seguimientoVerosimilitud.sinVerosimilitud.message'),
                    confirmText: t('alerts.seguimientoVerosimilitud.sinVerosimilitud.confirmText'),
                    cancelText: t('alerts.seguimientoVerosimilitud.sinVerosimilitud.cancelText'),
                    icon: 'warning'
                })
            );
            return null;
        }

        if (precarga.resultado === 'SIN_INCUMPLIMIENTOS') {
            dispatch(
                showAlert({
                    title: t('alerts.seguimientoVerosimilitud.sinIncumplimientos.title'),
                    message: t('alerts.seguimientoVerosimilitud.sinIncumplimientos.message'),
                    confirmText: t(
                        'alerts.seguimientoVerosimilitud.sinIncumplimientos.confirmText'
                    ),
                    icon: 'warning'
                })
            );
            return null;
        }

        return precarga;
    }, [dispatch, formData.idEstablecimientos, visitasPorEstablecimiento, t]);

    const handleTipoVisitaChange = useCallback(
        (newValue: Array<{ id: number; nombre: string; value: number }>) => {
            const idsActuales = new Set(formData.idTipoVisita.map((tipo: any) => tipo.id));
            const agregados = (newValue || []).filter((tipo) => !idsActuales.has(tipo.id));

            let valorSaneado = newValue || [];
            const mensajesConflicto: any[] = [];

            // El Seguimiento Verosimilitud tiene su propio chequeo de duplicados y precarga.
            const seAgregoSeguimiento = agregados.some(
                (tipo) => tipo.id === TIPO_VISITA_SEGUIMIENTO_VEROSIMILITUD_ID
            );
            const precargaSeguimiento = seAgregoSeguimiento
                ? validarSeguimientoVerosimilitud()
                : null;

            if (seAgregoSeguimiento && !precargaSeguimiento) {
                valorSaneado = valorSaneado.filter(
                    (tipo) => tipo.id !== TIPO_VISITA_SEGUIMIENTO_VEROSIMILITUD_ID
                );
            }

            agregados
                .filter((tipo) => tipo.id !== TIPO_VISITA_SEGUIMIENTO_VEROSIMILITUD_ID)
                .forEach((tipoAgregado) => {
                const conflictos = findTipoVisitaConflictos(tipoAgregado.id);
                if (conflictos.length === 0) return;

                // Se evita nombrar cada establecimiento (podían ser decenas): alcanza con la
                // cantidad, ya que el listado los destaca en naranja para identificarlos.
                const cantidad = conflictos.length;
                const mensaje = (
                    <span>
                        <strong>{cantidad}</strong> establecimiento{cantidad === 1 ? '' : 's'}{' '}
                        {cantidad === 1 ? 'ya tiene' : 'ya tienen'} una visita tipo{' '}
                        <strong>{tipoAgregado.nombre}</strong> en proceso. Se{' '}
                        {cantidad === 1 ? 'destacará' : 'destacarán'} con un borde naranja en el
                        listado de establecimientos seleccionados.
                    </span>
                );

                mensajesConflicto.push(mensaje);
            });

            if (mensajesConflicto.length > 0) {
                dispatch(
                    showAlert({
                        title: t('alerts.visitTypeInProgress.title'),
                        icon: 'info',
                        reactElement: (
                            <Box sx={{ textAlign: 'center' }}>
                                {mensajesConflicto.map((mensaje, index) => (
                                    <Box key={index} sx={{ mt: index === 0 ? 0 : 1 }}>
                                        <Typography
                                            variant='body2'
                                            component='div'
                                            sx={{
                                                fontSize: '18px',
                                                fontWeight: 500,
                                                color: 'var(--black)'
                                            }}
                                        >
                                            {mensaje}
                                        </Typography>
                                    </Box>
                                ))}
                                <Typography variant='body2' sx={{ mt: 1.5 }}>
                                    {t('alerts.visitTypeInProgress.instruction')}
                                </Typography>
                            </Box>
                        ),
                        confirmText: t('alerts.visitTypeInProgress.confirmText')
                    })
                );
            }

            if (precargaSeguimiento) {
                const hoy = dayjs().startOf('day');
                const { fechaSeguimiento } = precargaSeguimiento;

                setFechaMinimaSeguimiento(fechaSeguimiento);
                // La fecha límite no admite días pasados: si la de seguimiento ya venció, se
                // propone hoy.
                setFormData((prev) => ({
                    ...prev,
                    idTipoVisita: valorSaneado,
                    idProveedor: precargaSeguimiento.idProveedor,
                    idPreventor: precargaSeguimiento.preventor,
                    fechaLimite:
                        fechaSeguimiento && fechaSeguimiento.isAfter(hoy) ? fechaSeguimiento : hoy
                }));
                return;
            }

            if (!valorSaneado.some((tipo) => tipo.id === TIPO_VISITA_SEGUIMIENTO_VEROSIMILITUD_ID)) {
                setFechaMinimaSeguimiento(null);
            }

            setFormData((prev) => ({ ...prev, idTipoVisita: valorSaneado }));
        },
        [
            dispatch,
            formData.idTipoVisita,
            findTipoVisitaConflictos,
            t,
            validarSeguimientoVerosimilitud
        ]
    );

    const fechaLimiteAnteriorSeguimiento = Boolean(
        fechaMinimaSeguimiento &&
            formData.fechaLimite &&
            formData.fechaLimite.isBefore(fechaMinimaSeguimiento, 'day')
    );

    const handleGoBack = () => {
        if (isEditMode) {
            navigate('/backoffice/visitas');
            return;
        }

        if (linkedSiniestroId) {
            navigate('/backoffice/siniestros/detalle', {
                state: { siniestroId: linkedSiniestroId }
            });
            return;
        }

        if (!selectedEstablishmentFromDetail) {
            setStep(1);
            return;
        }

        navigate('/backoffice/visitas');
    };

    const handleSubmit = async () => {
        if (hayVisitasPendientesDeCargar) {
            enqueueSnackbar(t('alerts.visitTypeInProgress.pendingValidation'), {
                variant: 'warning'
            });
            return;
        }

        if (establecimientosIncompatibles.size > 0) {
            enqueueSnackbar(t('alerts.visitTypeInProgress.blockedSubmit'), { variant: 'error' });
            return;
        }

        const formattedData = formatDataForSubmission(formData, linkedSiniestroId);
        const isEditingVisit = isEditMode && Boolean(selectedVisitaFromState?.id);

        dispatch(
            showAlert({
                title: isEditingVisit ? t('alerts.editVisit.title') : t('alerts.generateVisit.title'),
                message: isEditingVisit
                    ? t('alerts.editVisit.message')
                    : t('alerts.generateVisit.message'),
                confirmAction: async () => {
                    // Revalida justo antes de pegarle al endpoint: cubre el caso de que el botón
                    // se haya habilitado a la fuerza (p.ej. removiendo el "disabled" por consola).
                    if (
                        hayVisitasPendientesDeCargar ||
                        establecimientosIncompatibles.size > 0
                    ) {
                        enqueueSnackbar(t('alerts.visitTypeInProgress.blockedSubmit'), {
                            variant: 'error'
                        });
                        return;
                    }

                    try {
                        if (isEditingVisit) {
                            await patchVisita({
                                idVisita: selectedVisitaFromState.id,
                                body: formattedData
                            }).unwrap();
                            enqueueSnackbar(t('alerts.editVisit.success'), { variant: 'success' });
                            navigate('/backoffice/visitas');
                            return;
                        }

                        await createVisita(formattedData).unwrap();
                        enqueueSnackbar(t('alerts.generateVisit.success'), { variant: 'success' });

                        if (linkedSiniestroId) {
                            navigate('/backoffice/siniestros/detalle', {
                                state: { siniestroId: linkedSiniestroId }
                            });
                            return;
                        }

                        navigate('/backoffice/visitas');
                    } catch (error: any) {
                        const fallbackMessage = isEditingVisit
                            ? t('alerts.editVisit.error')
                            : t('alerts.generateVisit.error');
                        const errorMessage = Array.isArray(error?.data?.message)
                            ? error.data.message.join('\n')
                            : error?.data?.message || fallbackMessage;

                        enqueueSnackbar(errorMessage, { variant: 'error' });
                    }
                },
                confirmText: isEditingVisit
                    ? t('alerts.editVisit.confirmText')
                    : t('alerts.generateVisit.confirmText'),
                cancelText: isEditingVisit
                    ? t('alerts.editVisit.cancelText')
                    : t('alerts.generateVisit.cancelText'),
                icon: 'info'
            })
        );
    };

    const handleCancel = () => {
        dispatch(
            showAlert({
                title: t('alerts.cancelVisit.title'),
                message: t('alerts.cancelVisit.message'),
                confirmAction: () => {
                    handleClearSelection();
                    navigate('/backoffice/visitas');
                },
                confirmText: t('alerts.cancelVisit.confirmText'),
                cancelText: t('alerts.cancelVisit.cancelText'),
                icon: 'info'
            })
        );
    };

    useEffect(() => {
        if ((isEditMode || isReadOnlyMode) && selectedVisitaFromState) {
            setStep(2);
            setFormData(mapVisitToFormData(selectedVisitaFromState, linkedEstablishmentId));
            return;
        }

        if (selectedEstablishmentFromDetail?.id && !linkedSiniestroId) {
            setStep(2);
            setFormData((prev) => ({
                ...prev,
                idEstablecimientos: prev.idEstablecimientos.includes(
                    selectedEstablishmentFromDetail.id
                )
                    ? prev.idEstablecimientos
                    : [...prev.idEstablecimientos, selectedEstablishmentFromDetail.id]
            }));
        }
    }, [
        isEditMode || isReadOnlyMode,
        linkedEstablishmentId,
        selectedEstablishmentFromDetail,
        selectedVisitaFromState,
        linkedSiniestroId
    ]);

    useEffect(() => {
        if (!linkedSiniestroId || !linkedEstablishmentId) return;

        setFormData((prev) => ({
            ...prev,
            idEstablecimientos: prev.idEstablecimientos.includes(linkedEstablishmentId)
                ? prev.idEstablecimientos
                : [...prev.idEstablecimientos, linkedEstablishmentId]
        }));
    }, [linkedEstablishmentId, linkedSiniestroId]);

    const esMortalLinked = linkedPatologias?.trim().toLowerCase() === 'muerte';
    const mostrarDistancia = Boolean(
        linkedSiniestroId &&
        ((linkedIdTipoSiniestro === 1 && esMortalLinked) || linkedIdTipoSiniestro === 3)
    );

    const isVisitDetailsInvalid =
        formData.idTipoVisita.length === 0 ||
        !formData.fechaLimite ||
        fechaLimiteAnteriorSeguimiento ||
        !formData.observaciones ||
        formData.idEstablecimientos.length === 0;

    const isNextDisabled =
        isVisitDetailsInvalid ||
        (!linkedEstablishmentId &&
            !linkedSiniestroId &&
            (!formData.nroContrato ||
                !formData.idOrganismo ||
                !formData.idProvincia ||
                !formData.ciudad));

    return {
        step,
        setStep,
        isEditMode,
        formData,
        handleChange,
        handleTipoVisitaChange,
        contracts,
        cities,
        provinces,
        organisms,
        handleContractSelection,
        establishments,
        selectedEstablishmentsDetails,
        handleSelectEstablishment,
        handleSelectEstablishments,
        handleClearSelection,
        handleSubmit,
        handleCancel,
        handleGoBack,
        isNextDisabled,
        isVisitDetailsInvalid,
        isFetchingContracts,
        loadingValidacionRGRL,
        handleFiltersEstablecimientosChange,
        filtersEstablecimientos,
        organizationsOptions,
        ciiuCodesOptions,
        isFetchingCiiuCodes,
        cleanEstablecimientosFilters,
        isFetchingEstablishments,
        isLoadingEstablishments,
        establecimientosPagination,
        establecimientosTotalCount,
        handleChangeEstablecimientosPage,
        handleChangeEstablecimientosRowsPerPage,
        isCreatingVisita: isLoading || isPatchingVisita,
        linkedIdTipoSiniestro,
        linkedPatologias,
        fechaLimiteEditadaManualmente,
        mostrarDistancia,
        visitasPorEstablecimiento,
        isFetchingVisitasEstablecimientos,
        fechaMinimaSeguimiento,
        fechaLimiteAnteriorSeguimiento,
        establecimientosIncompatibles,
        tiposVisitaIncompatibles,
        hayVisitasPendientesDeCargar
    };
}

export default useCreateVisita;
